import { useState, type ClipboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { ImageViewer } from './ImageViewer';
import { ErrorBanner, Field, Modal } from './ui';

/* Screenshots — migration 191. Matched to the API's limits, and checked here
   so somebody hears about a too-large image when they paste it, not on Send. */
const MAX_SHOTS = 6;
const MAX_SHOT_BYTES = 5 * 1024 * 1024;
const SHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

interface Shot {
  contentType: string;
  base64: string;
  /** The data: URL, for the thumbnail and the full-size view. */
  url: string;
}

/**
 * Feature request — in the top bar of every page, so the moment somebody
 * thinks "this screen should do X" there is somewhere to put it.
 *
 * THE PAGE IS CAPTURED, NOT TYPED. The one thing nobody remembers to include
 * is which screen they were looking at, and it is the thing that decides what
 * the request even means. It is shown read-only rather than hidden, so the
 * person can see what is being sent with it — and so they notice when they
 * have wandered off the page they meant to report.
 *
 * Who asked is taken from their session by the API and is not in this form.
 *
 * THE FORM IS PORTALLED TO THE BODY. This button lives in `.topbar`, which is
 * `position: sticky` with a z-index and therefore a stacking context of its
 * own: a dialog rendered where the button sits has its z-index capped at the
 * bar's, and the page covers it. Nothing is wrong with the dialog — it is
 * where it was mounted that decides whether it can be seen.
 */
export function FeatureRequestButton({ pageLabel }: { pageLabel: string | null }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen(true)}
        title="Ask for something on this page"
      >
        Feature request
      </button>
      {open &&
        createPortal(
          <FeatureRequestForm pageLabel={pageLabel} onClose={() => setOpen(false)} />,
          document.body,
        )}
    </>
  );
}

function FeatureRequestForm({ pageLabel, onClose }: { pageLabel: string | null; onClose: () => void }) {
  const { pathname, search } = useLocation();
  const pageUrl = `${pathname}${search}`;
  const [name, setName] = useState('');
  const [request, setRequest] = useState('');
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [viewing, setViewing] = useState<Shot | null>(null);

  /* Read each image to base64 and add it, refusing what the API would. */
  const addFiles = (files: File[]) => {
    for (const file of files) {
      if (!SHOT_TYPES.includes(file.type)) {
        setError('A screenshot must be a PNG, JPEG, GIF or WebP image.');
        continue;
      }
      if (file.size > MAX_SHOT_BYTES) {
        setError('That screenshot is larger than 5 MB.');
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const url = String(reader.result);
        setShots((current) =>
          current.length >= MAX_SHOTS
            ? current
            : [...current, { contentType: file.type, base64: url.split(',')[1] ?? '', url }],
        );
      };
      reader.readAsDataURL(file);
    }
  };

  /* Ctrl+V anywhere in the form. Only images are taken over — pasted text
     goes into whichever field has focus, as it always has. */
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const images = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (!images.length) return;
    e.preventDefault();
    if (shots.length + images.length > MAX_SHOTS) setError(`Up to ${MAX_SHOTS} screenshots on one request.`);
    addFiles(images);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/feature-requests', {
        name,
        request,
        pageUrl,
        pageLabel,
        images: shots.map((s) => ({ contentType: s.contentType, base64: s.base64 })),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the request');
      setSaving(false);
    }
  };

  /* Said and done, rather than a form that empties itself and leaves somebody
     wondering whether it went. */
  if (sent) {
    return (
      <Modal
        title="Thank you"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <p className="small">
          Your request is in. It goes to the Feature requests list, with the page you were on.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Feature request"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim() || !request.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div onPaste={onPaste}>
        {error && <ErrorBanner message={error} />}
        <Field label="Page" hint="Taken from where you are. Sent with the request.">
          <input className="input" value={pageLabel ? `${pageLabel} — ${pageUrl}` : pageUrl} readOnly />
        </Field>
        <Field label="Name">
          <input
            className="input"
            autoFocus
            placeholder="What to call it, in a few words"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Feature request">
          <textarea
            className="textarea"
            rows={6}
            placeholder="What you would like it to do, and what you are trying to get done."
            value={request}
            onChange={(e) => setRequest(e.target.value)}
          />
        </Field>
        <Field
          label="Screenshots"
          hint={`Paste with Ctrl+V anywhere in this form, or add a file. Up to ${MAX_SHOTS}, 5 MB each. Click one to see it full size.`}
        >
          <div className="shot-drop">
            {shots.length > 0 && (
              <div className="shot-thumbs mb-2">
                {shots.map((shot, i) => (
                  <div key={shot.url.slice(-40) + i} style={{ position: 'relative' }}>
                    <button type="button" className="shot-thumb" onClick={() => setViewing(shot)} title="See it full size">
                      <img src={shot.url} alt={`Screenshot ${i + 1}`} />
                    </button>
                    <button
                      type="button"
                      className="shot-thumb-remove"
                      aria-label={`Remove screenshot ${i + 1}`}
                      onClick={() => setShots((current) => current.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className={`btn btn-ghost btn-sm${shots.length >= MAX_SHOTS ? ' disabled' : ''}`}>
              Add screenshot
              <input
                type="file"
                accept={SHOT_TYPES.join(',')}
                multiple
                style={{ display: 'none' }}
                disabled={shots.length >= MAX_SHOTS}
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </label>
            {shots.length === 0 && <span className="xs muted" style={{ marginLeft: 'var(--space-2)' }}>or paste one here</span>}
          </div>
        </Field>
      </div>
      {viewing && <ImageViewer src={viewing.url} alt="Screenshot" onClose={() => setViewing(null)} />}
    </Modal>
  );
}
